import { memo, useMemo, useCallback, useState } from 'react';
import useSWR from 'swr';
import { FormikConfig } from 'formik';
import { Button } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import axios from 'axios';
import { IWorkflow } from '@modules/admin/AdminTools/Workflows/types';
import { ILeadSource } from '@modules/admin/AdminTools/CreateContact/queries';
import { gridDateTimeFormatter } from '@shared/commonFunctions';
import { IPhoenixGridActions, PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { dataGridCol } from '@shared/components/grid/common';
import { EC_SERVICE, SERVICES } from '@shared/constants/apiConfig';
import { useLeadSource } from '@shared/hooks/useLeadSource';
import { Wrapper } from '@styles/CommonStyles';
import { EditCommunicationModal } from './components/EditCommunicationModal';
import { requestCreateCommunication, requestDeleteCommunication, requestUpdateCommunication } from './requests';
import {
  ICommunicationValues,
  IEmail,
  IEmailTemplate,
  IEmailTemplates,
  ISid,
  ISMSTemplate,
  ISMSTemplates,
  IWorkflowCommunication,
  TShortWorkflowStep,
} from './types';
import { ConfigureSidsModal } from './components/ConfigureSidsModal';
import { ConfigureEmailsModal } from './components/ConfigureEmailsModal';

const getColumnsData = (workFlowSteps: TShortWorkflowStep[], leadSources: ILeadSource[]): GridColDef[] => {
  const columnsData: GridColDef[] = [
    {
      field: 'numericId',
      headerName: 'ID',
      minWidth: 10,
    },
    {
      field: 'isSms',
      headerName: 'SMS Text',
      type: 'boolean',
    },
    {
      field: 'isEmail',
      headerName: 'Email',
      type: 'boolean',
    },
    {
      field: 'delayInMinutes',
      headerName: 'Delay',
    },
    {
      field: 'excludedStates',
      minWidth: 150,
      headerName: 'Excluded States',
      valueGetter: (cellParams) => {
        const states: string[] = cellParams.value ?? [];
        return states.join(',');
      },
    },
    // Todo: to be used in future.
    // {
    //   field: 'includedLenders',
    //   minWidth: 150,
    //   headerName: 'Included Lenders',
    //   valueGetter: (cellParams) => {
    //     const lenders: string[] = cellParams.value ?? [];
    //     return lenders.map((lender: string) => {
    //       const currentStep = lenderList?.find(a => a.id == lender);
    //       return currentStep ? `${currentStep.code},`  : '';
    //     });
    //   },
    // },
    {
      field: 'includedLeadSources',
      minWidth: 150,
      headerName: 'Sources',
      valueGetter: (cellParams) => {
        const sources: string[] = cellParams.value ?? [];
        return sources
          .map((source: string) => {
            const currentStep = leadSources?.find((a) => a.id == source);
            return currentStep?.name;
          })
          .join(', ');
      },
    },
    {
      field: 'workflowSteps',
      headerName: 'Workflow Steps',
      minWidth: 350,
      valueGetter: (cellParams) => {
        const stepIds: string[] = cellParams.value ?? [];

        return stepIds.map((stepId) => {
          const currentStep = workFlowSteps.find(({ id }) => stepId === id);

          return currentStep ? `${currentStep.name}[${currentStep.numericId}]` : '';
        });
      },
    },
    {
      field: 'updatedDate',
      headerName: 'Updated Date',
      minWidth: 150,
      type: 'dateTime',
      valueFormatter: gridDateTimeFormatter,
    },
    {
      field: 'updatedBy',
      minWidth: 150,
      headerName: 'Updated By',
    },
  ];

  return columnsData.map(({ field, headerName, ...props }) =>
    dataGridCol(field, {
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      headerName,
      ...props,
    })
  );
};

const CustomerCommunicationsContainer = () => {
  const [currentCommunication, setCurrentCommunication] = useState<IWorkflowCommunication | null>(null);
  const [editCommunicationModalOpen, setEditCommunicationModalOpen] = useState(0);
  const [configureEmailsModalOpen, setConfigureEmailsModalOpen] = useState(0);
  const [configureSidModalOpen, setConfigureSidModalOpen] = useState(0);
  const { leadSourceData: sources } = useLeadSource<ILeadSource[]>();

  const { data: communications, mutate: refetchCommunications } = useSWR<IWorkflowCommunication[]>(
    `${SERVICES.WORKFLOW}/communications`,
    { revalidateOnMount: undefined }
  );

  // const { data: lenderList } = useSWR<ILenderListItem[]>(`/${SERVICES.LENDER }/lenders`);

  const { data: workflowSteps } = useSWR<TShortWorkflowStep[]>(`${SERVICES.WORKFLOW}/steps/all`);

  const { data: workflowTree } = useSWR<IWorkflow[]>(`${SERVICES.WORKFLOW}/workflows/tree`);

  const { data: emails, error: emailError, mutate: emailMutate } = useSWR<IEmail[]>(`${EC_SERVICE.ENTERPRISECOMMUNICATION}/emailconfiguration`);

  const {
    data: sids,
    error: sidError,
    mutate: sidMutate,
  } = useSWR<ISid[]>(`${EC_SERVICE.ENTERPRISECOMMUNICATION}/sidconfiguration`);

  const fetchWithoutOneClick = async <T extends { isOneClickTemplate?: boolean }>(url: string) => {
    const { data } = await axios.get(url);
    return data?.filter((template: T) => !template.isOneClickTemplate);
  };

  const { data: emailTemplates, mutate: refetchEmailTemplates } = useSWR<IEmailTemplates>(
    `${EC_SERVICE.ENTERPRISECOMMUNICATION}/emailtemplate/all`,
    fetchWithoutOneClick<IEmailTemplate>
  );

  const { data: smsTemplates, mutate: refetchSmsTemplates } = useSWR<ISMSTemplates>(
    `${EC_SERVICE.ENTERPRISECOMMUNICATION}/smstemplate`,
    fetchWithoutOneClick<ISMSTemplate>
  );


  const onOpenEditCommunicationModal = useCallback((communicationData: IWorkflowCommunication | null) => {
    setCurrentCommunication(communicationData);
    setEditCommunicationModalOpen((prevState) => prevState + 1);
  }, []);

  const onCloseCommunicationModal = useCallback(() => {
    setCurrentCommunication(null);
    setEditCommunicationModalOpen(0);
  }, []);

  const onSubmitCommunication = useCallback<FormikConfig<ICommunicationValues>['onSubmit']>(
    async ({ id, workflowIds: _workflowIds, workflowSteps: workflowStepsValue, ...values }): Promise<void> => {
      const workflowSteps = workflowStepsValue.filter((step) => !!step) as string[];
      const updatedValues: Partial<IWorkflowCommunication> = {
        ...values,
        workflowSteps,
      };

      if (id) {
        await requestUpdateCommunication({
          ...currentCommunication,
          ...updatedValues,
        } as IWorkflowCommunication);
      } else {
        await requestCreateCommunication(updatedValues);
      }

      await refetchCommunications();
      await refetchEmailTemplates();
      await refetchSmsTemplates();
      setCurrentCommunication(null);
      onCloseCommunicationModal();
    },
    [refetchCommunications, onCloseCommunicationModal, currentCommunication]
  );

  const columns = useMemo<GridColDef[]>(
    () => (workflowSteps ? getColumnsData(workflowSteps, sources ?? []) : []),
    [workflowSteps, sources]
  );

  const handleDeleteCommuntaction = useCallback(
    async (id: string) => {
      await requestDeleteCommunication(id);
      refetchCommunications();
    },
    [refetchCommunications]
  );

  const gridActions = useMemo<IPhoenixGridActions>(
    () => ({
      onEdit: (row: IWorkflowCommunication): void => onOpenEditCommunicationModal(row),
      onDelete: (row: IWorkflowCommunication): Promise<void> => handleDeleteCommuntaction(row.id),
    }),
    [onOpenEditCommunicationModal]
  );

  const buttonsGroup = (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setConfigureEmailsModalOpen((prevState) => prevState + 1)}
      >
        Configure On Behalf Email
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setConfigureSidModalOpen((prevState) => prevState + 1)}
      >
        Configure On Behalf SID
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() => onOpenEditCommunicationModal(null)}
      >
        Add New Record
      </Button>
    </>
  );

  return (
    <Wrapper>
      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        noButton
        showClearAllFilter
        name="Customer Communication"
        pageSize={25}
        columns={columns}
        loading={!communications}
        headerContent={buttonsGroup}
        rows={communications ?? []}
        actions={gridActions}
      />

      {workflowSteps && workflowTree && !!editCommunicationModalOpen && (
        <EditCommunicationModal
          emails={emails ?? []}
          sids={sids ?? []}
          emailTemplates={emailTemplates ?? []}
          smsTemplates={smsTemplates ?? []}
          emailError={emailError}
          sidsError={sidError}
          editCommunicationModalPosition={editCommunicationModalOpen}
          currentCommunication={currentCommunication}
          workflowSteps={workflowSteps}
          workflowTree={workflowTree}
          onClose={onCloseCommunicationModal}
          onSubmit={onSubmitCommunication}
        />
      )}

      {!!configureEmailsModalOpen && (
        <ConfigureEmailsModal
          confiqureEmailModalPosition={configureEmailsModalOpen}
          emails={emails}
          error={emailError}
          mutate={emailMutate}
          onClose={() => setConfigureEmailsModalOpen(0)}
        />
      )}
      {!!configureSidModalOpen && (
        <ConfigureSidsModal
          configureSidModalPosition={configureSidModalOpen}
          sids={sids}
          error={sidError}
          mutate={sidMutate}
          onClose={() => setConfigureSidModalOpen(0)}
        />
      )}
    </Wrapper>
  );
};

export const CustomerCommunications = memo(CustomerCommunicationsContainer);
